import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { services, serviceCategories } from '@/mocks/services';

export default function Soins() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedService, setExpandedService] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const filteredServices = activeCategory === 'all'
    ? services
    : services.filter((s) => s.category === activeCategory);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.reveal-item');
            elements.forEach((el, i) => {
              setTimeout(() => { el.classList.add('visible'); }, i * 100);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [activeCategory]);

  useEffect(() => {
    const cards = document.querySelectorAll('.reveal-item');
    cards.forEach((el) => el.classList.remove('visible'));
    const timer = setTimeout(() => {
      cards.forEach((el, i) => {
        setTimeout(() => { el.classList.add('visible'); }, i * 80);
      });
    }, 50);
    return () => clearTimeout(timer);
  }, [activeCategory]);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative w-full h-[55vh] min-h-[380px] overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Luxury%20spa%20products%20flat%20lay%20arrangement%2C%20elegant%20glass%20serum%20bottles%2C%20fresh%20white%20roses%2C%20gold%20accessories%2C%20white%20marble%20surface%2C%20soft%20warm%20studio%20lighting%2C%20high-end%20beauty%20editorial%20photography%2C%20beige%20cream%20and%20gold%20tones&width=1600&height=700&seq=kds-soins-hero&orientation=landscape"
            alt="Soins KD'S BEAUTY"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-foreground-950/50" />
        </div>
        <div className="relative z-10 text-center px-4 w-full">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/10 backdrop-blur-sm border border-background-50/20 mb-5">
            <span className="text-background-50/90 text-xs tracking-[0.15em] uppercase font-label">Catalogue complet</span>
          </div>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl text-background-50 font-light mb-4">Nos Soins & Prestations</h1>
          <p className="text-background-50/70 text-base md:text-lg max-w-2xl mx-auto">
            Des protocoles d'exception conçus pour révéler votre beauté naturelle
          </p>
        </div>
      </section>

      {/* Category Filters */}
      <section className="w-full py-6 bg-background-50 border-b border-background-200/60 sticky top-16 md:top-20 z-20">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex items-center gap-2 md:gap-3 overflow-x-auto max-w-6xl mx-auto pb-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {serviceCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer border ${
                  activeCategory === cat.id
                    ? 'bg-primary-500 text-background-50 border-primary-500 shadow-sm'
                    : 'bg-background-50 text-foreground-700 border-background-300/80 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section ref={sectionRef} className="w-full py-12 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto">
            {filteredServices.map((service) => (
              <div
                key={service.id}
                className="reveal-item opacity-0 translate-y-6 transition-all duration-700"
                style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
              >
                <div
                  className="bg-background-50 rounded-2xl overflow-hidden border border-background-200/60 hover:border-primary-300/70 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl h-full flex flex-col cursor-pointer"
                  onClick={() => setExpandedService(expandedService === service.id ? null : service.id)}
                >
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="inline-block px-3 py-1.5 text-xs font-medium bg-background-50/90 backdrop-blur-sm text-foreground-700 rounded-full border border-background-200/50">
                        {service.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5 md:p-6 flex flex-col flex-1">
                    <h3 className="font-heading text-xl text-foreground-800 mb-2">{service.name}</h3>
                    <p className={`text-sm text-foreground-600 leading-relaxed mb-4 flex-1 ${expandedService === service.id ? '' : 'line-clamp-2'}`}>
                      {service.description}
                    </p>

                    {expandedService === service.id && (
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Bienfaits</h4>
                        <ul className="space-y-2">
                          {service.benefits.map((benefit, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-foreground-600">
                              <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0 rounded-full bg-secondary-50">
                                <i className="ri-check-line text-secondary-500 text-xs" />
                              </div>
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-background-200/50 mt-auto">
                      <div className="flex items-center gap-2 text-sm text-foreground-500">
                        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line text-xs" /></div>
                        <span>{service.duration} min</span>
                      </div>
                      <span className="text-xl font-heading text-primary-600">
                        {service.price.toLocaleString('fr-FR')} FCFA
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredServices.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-search-line text-2xl text-foreground-400" />
              </div>
              <p className="text-foreground-500">Aucun soin trouvé dans cette catégorie</p>
            </div>
          )}
        </div>
      </section>

      {/* Forfaits */}
      <section className="w-full py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-4 tracking-wider uppercase">Forfaits</span>
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-800 mb-3">Forfaits Bien-Être</h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              Des rituels combinés pour une expérience complète et un prix préférentiel
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
            {[
              { name: 'Rituel Éclat', price: 45000, items: 'Soin visage + Manucure', duration: '2h', icon: 'ri-star-line' },
              { name: 'Rituel Sérénité', price: 55000, items: 'Massage relaxant + Soin visage', duration: '2h30', icon: 'ri-heart-pulse-line' },
              { name: 'Rituel Prestige', price: 75000, items: 'Massage + Soin visage + Manucure', duration: '3h30', icon: 'ri-vip-crown-line' },
            ].map((pack, i) => (
              <div key={i} className="bg-background-50 rounded-2xl p-6 md:p-8 text-center border border-background-200/60 hover:border-primary-300/70 hover:-translate-y-2 hover:shadow-lg transition-all duration-500 group">
                <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-full bg-primary-50 group-hover:bg-primary-500 transition-colors duration-500">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${pack.icon} text-xl text-primary-500 group-hover:text-background-50 transition-colors duration-500`} />
                  </div>
                </div>
                <h3 className="font-heading text-xl text-foreground-800 mb-2">{pack.name}</h3>
                <p className="text-sm text-foreground-600 mb-1">{pack.items}</p>
                <p className="text-xs text-foreground-400 mb-5">{pack.duration}</p>
                <p className="text-2xl font-heading text-primary-600 mb-6">{pack.price.toLocaleString('fr-FR')} FCFA</p>
                <Link to="/rdv" className="inline-flex items-center justify-center px-6 py-2.5 rounded-full text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-300">
                  Réserver
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full py-16 bg-primary-500">
        <div className="w-full px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-background-50 mb-4">Prête à rayonner ?</h2>
          <p className="text-background-50/80 max-w-xl mx-auto mb-8">
            Prenez rendez-vous dès maintenant et offrez-vous un moment de pure beauté
          </p>
          <Link to="/rdv" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-medium bg-background-50 text-foreground-800 hover:bg-foreground-50 transition-all duration-300">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-whatsapp-line text-lg" />
            </div>
            Prendre rendez-vous
          </Link>
        </div>
      </section>
    </Layout>
  );
}